import { supabase } from '../lib/supabase';
import Papa from 'papaparse';

export const adminService = {
  // FIXED 2026-01-30: Admin check now properly validates user_profiles.role column
  // Migration 20260130020300 updated is_admin_from_auth() to check:
  // 1. user_profiles.role = 'admin' (PRIMARY - for real admin users)
  // 2. auth.users metadata (FALLBACK - for demo/legacy admin)
  // 3. Hardcoded UUIDs (LEGACY - for backward compatibility)
  // SIMPLIFIED and ENHANCED admin check - works for both demo and real admin
  async isAdmin() {
    try {
      // PRIORITY 1: Check demo admin from window context (set by AuthContext)
      const authContext = this.getDemoAdminContext();
      //if (authContext?.isDemoAdmin && authContext?.userProfile?.role === 'admin') {
      if (authContext?.userProfile?.role === 'admin') {
        return true;
      }
      
      // PRIORITY 2: Check for demo admin in localStorage as fallback
      const demoSession = this.getDemoSessionFromStorage();
      if (demoSession?.role === 'admin') {
        return true;
      }
      
      // PRIORITY 3: Check real Supabase user
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return false;
      
      // Use the enhanced database function for robust admin check
      const { data, error } = await supabase?.rpc('is_user_admin', { user_uuid: user?.id });
        
      if (error) {
        console.error('Admin check error:', error);
        return false;
      }
      
      return data === true;
    } catch (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
  },

  // ENHANCED: Helper method to get demo admin context from window (set by AuthContext)
  getDemoAdminContext() {
    try {
      if (typeof window !== 'undefined' && window.authContext) {
        return window.authContext;
      }
      return null;
    } catch (error) {
      return null;
    }
  },

  // Helper method to get demo session from localStorage
  getDemoSessionFromStorage() {
    try {
      const demoSession = localStorage.getItem('demoAdminSession');
      if (demoSession) {
        return JSON.parse(demoSession);
      }
      return null;
    } catch (error) {
      return null;
    }
  },

  // Helper method to check demo admin status from AuthContext - IMPROVED
  async checkDemoAdmin() {
    try {
      // Primary: Try to access the auth context if available (set by Header component)
      const authContext = this.getDemoAdminContext();
      if (authContext) {
        return authContext;
      }
      
      // Fallback: check localStorage for demo admin session
      const demoSession = this.getDemoSessionFromStorage();
      if (demoSession) {
        return {
          isDemoAdmin: true,
          userProfile: demoSession,
          user: demoSession
        };
      }
      
      return null;
    } catch (error) {
      console.warn('Demo admin check failed:', error);
      return null;
    }
  },

  // Get dashboard statistics
  async getDashboardStats() {
    try {
      const { data, error } = await supabase?.rpc('get_admin_dashboard_stats');
        
      if (error) throw error;
      
      const row = data?.[0] || {};
      return {
        totalUsers: row?.total_users ?? 0,
        totalVerses: row?.total_verses ?? 0,
        totalBooks: row?.total_books ?? 0,
        activeImports: row?.active_imports ?? 0,
        recentImports: row?.recent_imports ?? 0
      };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error;
    }
  },

  // Get import history
  async getImportHistory(limit = 20) {
    try {
      const { data, error } = await supabase?.from('bible_import_logs')?.select(`
          id,
          file_name,
          file_size,
          file_type,
          status,
          total_verses,
          processed_verses,
          error_message,
          started_at,
          completed_at,
          admin_user_id,
          version_id,
          version_name,
          short_version_name,
          user_profiles!bible_import_logs_admin_user_id_fkey(first_name, last_name, email)
        `)?.order('started_at', { ascending: false })?.limit(limit);
        
      if (error) throw error;
      
      // Convert to camelCase
      return data?.map(log => ({
        id: log?.id,
        fileName: log?.file_name,
        fileSize: log?.file_size,
        fileType: log?.file_type,
        status: log?.status,
        totalVerses: log?.total_verses,
        processedVerses: log?.processed_verses,
        errorMessage: log?.error_message,
        startedAt: log?.started_at,
        completedAt: log?.completed_at,
        adminUserId: log?.admin_user_id,
        versionId: log?.version_id,
        versionName: log?.version_name,
        shortVersionName: log?.short_version_name,
        adminUser: log?.user_profiles
      })) || [];
    } catch (error) {
      console.error('Error fetching import history:', error);
      throw error;
    }
  },

  // Upload Bible import file
  async uploadImportFile(file) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      // Validate file
      if (!file) throw new Error('No file provided');
      if (file?.size > 10 * 1024 * 1024) throw new Error('File size exceeds 10MB limit');
      
      const allowedTypes = ['text/csv', 'application/json', 'text/plain'];
      if (!allowedTypes?.includes(file?.type)) {
        throw new Error('Invalid file type. Only CSV and JSON files are allowed.');
      }

      // Generate unique filename
      const timestamp = new Date()?.toISOString()?.replace(/[:.]/g, '-');
      const filePath = `${user?.id}/${timestamp}-${file?.name}`;
      
      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase?.storage?.from('bible-imports')?.upload(filePath, file);
        
      if (uploadError) throw uploadError;
      
      return {
        path: uploadData?.path,
        fileName: file?.name,
        fileSize: file?.size,
        fileType: file?.type?.includes('json') ? 'json' : 'csv'
      };
    } catch (error) {
      console.error('Error uploading import file:', error);
      throw error;
    }
  },

  // Enhanced CSV parsing with header autodetection
  parseCSVContent(csvContent) {
    return new Promise((resolve, reject) => {
      Papa.parse(csvContent, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          if (results.errors?.length > 0) {
            reject(new Error(`CSV parsing errors: ${results.errors.map(e => e.message).join(', ')}`));
            return;
          }
          
          // Get actual headers from the file
          const detectedHeaders = results.meta?.fields || [];
          
          // Auto-detect header variations for Bible import
          const headerMappings = {
            verse_id: ['Verse ID', 'verse_id', 'id', 'VerseID', 'verse id'],
            book_name: ['Book Name', 'book_name', 'book', 'BookName', 'book name'],
            book_number: ['Book Number', 'book_number', 'booknum', 'BookNumber', 'book number'],
            chapter: ['Chapter', 'chapter', 'chap', 'Chapter Number'],
            verse: ['Verse', 'verse', 'verse_number', 'VerseNumber', 'verse number'],
            text: ['Text', 'text', 'verse_text', 'VerseText', 'verse text', 'content']
          };
          
          // Find matching headers
          const mappedHeaders = {};
          Object.keys(headerMappings).forEach(field => {
            const foundHeader = detectedHeaders.find(header => 
              headerMappings[field].some(variant => 
                header.toLowerCase().trim() === variant.toLowerCase().trim()
              )
            );
            if (foundHeader) {
              mappedHeaders[field] = foundHeader;
            }
          });
          
          // Check if we have the minimum required headers
          const requiredFields = ['book_name', 'chapter', 'verse', 'text'];
          const missingFields = requiredFields.filter(field => !mappedHeaders[field]);
          
          if (missingFields.length > 0) {
            reject(new Error(`Missing required fields: ${missingFields.join(', ')}. Detected headers: ${detectedHeaders.join(', ')}`));
            return;
          }
          
          // Convert CSV data to standard format using detected headers
          let verses = results.data.map((row, index) => ({
            verse_id: row[mappedHeaders.verse_id] || row[mappedHeaders.book_name] + '_' + row[mappedHeaders.chapter] + '_' + row[mappedHeaders.verse],
            book_name: row[mappedHeaders.book_name]?.trim(),
            book_number: parseInt(row[mappedHeaders.book_number]) || this.getBookNumber(row[mappedHeaders.book_name]),
            chapter: parseInt(row[mappedHeaders.chapter]),
            verse: parseInt(row[mappedHeaders.verse]),
            text: row[mappedHeaders.text]?.trim(),
            row_index: index + 1
          }));
          
          // Filter out invalid rows
          verses = verses.filter(verse => 
            verse.book_name && 
            !isNaN(verse.chapter) && 
            !isNaN(verse.verse) && 
            verse.text
          );
          
          resolve({
            verses,
            detectedHeaders,
            mappedHeaders,
            totalRows: verses.length
          });
        },
        error: (error) => {
          reject(new Error(`CSV parsing failed: ${error.message}`));
        }
      });
    });
  },

  // Helper function to get book number from book name
  getBookNumber(bookName) {
    const bookNumbers = {
      // English names
      'Genesis': 1, 'Exodus': 2, 'Leviticus': 3, 'Numbers': 4, 'Deuteronomy': 5,
      'Joshua': 6, 'Judges': 7, 'Ruth': 8, '1 Samuel': 9, '2 Samuel': 10,
      '1 Kings': 11, '2 Kings': 12, '1 Chronicles': 13, '2 Chronicles': 14, 'Ezra': 15,
      'Nehemiah': 16, 'Esther': 17, 'Job': 18, 'Psalms': 19, 'Proverbs': 20,
      'Ecclesiastes': 21, 'Song of Solomon': 22, 'Isaiah': 23, 'Jeremiah': 24, 'Lamentations': 25,
      'Ezekiel': 26, 'Daniel': 27, 'Hosea': 28, 'Joel': 29, 'Amos': 30,
      'Obadiah': 31, 'Jonah': 32, 'Micah': 33, 'Nahum': 34, 'Habakkuk': 35,
      'Zephaniah': 36, 'Haggai': 37, 'Zechariah': 38, 'Malachi': 39,
      'Matthew': 40, 'Mark': 41, 'Luke': 42, 'John': 43, 'Acts': 44,
      'Romans': 45, '1 Corinthians': 46, '2 Corinthians': 47, 'Galatians': 48, 'Ephesians': 49,
      'Philippians': 50, 'Colossians': 51, '1 Thessalonians': 52, '2 Thessalonians': 53, '1 Timothy': 54,
      '2 Timothy': 55, 'Titus': 56, 'Philemon': 57, 'Hebrews': 58, 'James': 59,
      '1 Peter': 60, '2 Peter': 61, '1 John': 62, '2 John': 63, '3 John': 64,
      'Jude': 65, 'Revelation': 66,
      // French names
      'Genèse': 1, 'Exode': 2, 'Lévitique': 3, 'Nombres': 4, 'Deutéronome': 5,
      'Josué': 6, 'Juges': 7, 'Ruth': 8, '1 Samuel': 9, '2 Samuel': 10,
      '1 Rois': 11, '2 Rois': 12, '1 Chroniques': 13, '2 Chroniques': 14,
      'Esdras': 15, 'Néhémie': 16, 'Esther': 17, 'Job': 18,
      'Psaume': 19, 'Psaumes': 19, 'Proverbes': 20, 'Ecclésiaste': 21,
      'Cantique Des Cantiqu': 22, 'Cantique des Cantiques': 22,
      'Ésaïe': 23, 'Esaïe': 23, 'Isaïe': 23,
      'Jérémie': 24, 'Lamentations': 25, 'Ézéchiel': 26, 'Ezéchiel': 26,
      'Daniel': 27, 'Osée': 28, 'Joël': 29, 'Amos': 30, 'Abdias': 31,
      'Jonas': 32, 'Michée': 33, 'Nahum': 34, 'Habacuc': 35, 'Sophonie': 36,
      'Aggée': 37, 'Zacharie': 38, 'Malachie': 39,
      'Matthieu': 40, 'Marc': 41, 'Luc': 42, 'Jean': 43, 'Actes': 44,
      'Romains': 45, '1 Corinthiens': 46, '2 Corinthiens': 47, 'Galates': 48,
      'Éphésiens': 49, 'Ephésiens': 49, 'Philippiens': 50, 'Colossiens': 51,
      '1 Thessaloniciens': 52, '2 Thessaloniciens': 53, '1 Timothée': 54, '2 Timothée': 55,
      'Tite': 56, 'Philémon': 57, 'Hébreux': 58, 'Jacques': 59,
      '1 Pierre': 60, '2 Pierre': 61, '1 Jean': 62, '2 Jean': 63, '3 Jean': 64,
      'Jude': 65, 'Apocalypse': 66
    };
    
    return bookNumbers?.[bookName] || 0;
  },

  // English standard book names indexed by book number (1-66)
  getEnglishBookName(bookNumber) {
    const englishNames = [
      '', // index 0 unused
      'Genesis', 'Exodus', 'Leviticus', 'Numbers', 'Deuteronomy',
      'Joshua', 'Judges', 'Ruth', '1 Samuel', '2 Samuel',
      '1 Kings', '2 Kings', '1 Chronicles', '2 Chronicles', 'Ezra',
      'Nehemiah', 'Esther', 'Job', 'Psalms', 'Proverbs',
      'Ecclesiastes', 'Song of Solomon', 'Isaiah', 'Jeremiah', 'Lamentations',
      'Ezekiel', 'Daniel', 'Hosea', 'Joel', 'Amos',
      'Obadiah', 'Jonah', 'Micah', 'Nahum', 'Habakkuk',
      'Zephaniah', 'Haggai', 'Zechariah', 'Malachi',
      'Matthew', 'Mark', 'Luke', 'John', 'Acts',
      'Romans', '1 Corinthians', '2 Corinthians', 'Galatians', 'Ephesians',
      'Philippians', 'Colossians', '1 Thessalonians', '2 Thessalonians', '1 Timothy',
      '2 Timothy', 'Titus', 'Philemon', 'Hebrews', 'James',
      '1 Peter', '2 Peter', '1 John', '2 John', '3 John',
      'Jude', 'Revelation'
    ];
    return englishNames?.[bookNumber] || null;
  },

  // Build a chapter_id lookup map by creating bible_books and bible_chapters as needed.
  // Returns a map: { "bookNumber_chapterNum": chapter_id }
  async buildChapterIdMap(verses) {
    // Collect unique (bookNumber, bookName, chapter) combinations
    const bookChapterSet = new Map(); // key: "bookNum_chap", value: { bookNumber, bookName, chapter }
    const bookMap = new Map(); // key: bookNumber, value: bookName (first seen)

    for (const v of verses) {
      const bookNum = parseInt(v?.book_number) || this.getBookNumber(v?.book_name);
      const chap = parseInt(v?.chapter);
      if (!bookNum || !chap) continue;

      if (!bookMap?.has(bookNum)) {
        bookMap?.set(bookNum, v?.book_name);
      }
      const key = `${bookNum}_${chap}`;
      if (!bookChapterSet?.has(key)) {
        bookChapterSet?.set(key, { bookNumber: bookNum, bookName: v?.book_name, chapter: chap });
      }
    }

    const chapterIdMap = new Map(); // key: "bookNum_chap", value: chapter_id

    // For each unique book, ensure bible_books record exists
    const bookIdMap = new Map(); // bookNumber → bible_books.id

    for (const [bookNum, bookName] of bookMap?.entries()) {
      // Use English standard name for the shared bible_books table
      const englishName = this.getEnglishBookName(bookNum) || bookName;

      // Try to find existing book by book_number
      const { data: existingBook } = await supabase
        ?.from('bible_books')
        ?.select('id')
        ?.eq('book_number', bookNum)
        ?.maybeSingle();

      if (existingBook?.id) {
        bookIdMap?.set(bookNum, existingBook?.id);
      } else {
        // Insert the book using English name
        const abbreviations = {
          1:'Gen',2:'Exod',3:'Lev',4:'Num',5:'Deut',6:'Josh',7:'Judg',8:'Ruth',
          9:'1Sam',10:'2Sam',11:'1Kgs',12:'2Kgs',13:'1Chr',14:'2Chr',15:'Ezra',
          16:'Neh',17:'Esth',18:'Job',19:'Ps',20:'Prov',21:'Eccl',22:'Song',
          23:'Isa',24:'Jer',25:'Lam',26:'Ezek',27:'Dan',28:'Hos',29:'Joel',30:'Amos',
          31:'Obad',32:'Jonah',33:'Mic',34:'Nah',35:'Hab',36:'Zeph',37:'Hag',
          38:'Zech',39:'Mal',40:'Matt',41:'Mark',42:'Luke',43:'John',44:'Acts',
          45:'Rom',46:'1Cor',47:'2Cor',48:'Gal',49:'Eph',50:'Phil',51:'Col',
          52:'1Thess',53:'2Thess',54:'1Tim',55:'2Tim',56:'Titus',57:'Phlm',
          58:'Heb',59:'Jas',60:'1Pet',61:'2Pet',62:'1John',63:'2John',64:'3John',
          65:'Jude',66:'Rev'
        };
        const testaments = bookNum <= 39 ? 'old' : 'new';
        const chapterCounts = {
          1:50,2:40,3:27,4:36,5:34,6:24,7:21,8:4,9:31,10:24,11:22,12:25,13:29,14:36,
          15:10,16:13,17:10,18:42,19:150,20:31,21:12,22:8,23:66,24:52,25:5,26:48,
          27:12,28:14,29:3,30:9,31:1,32:4,33:7,34:3,35:3,36:3,37:2,38:14,39:4,
          40:28,41:16,42:24,43:21,44:28,45:16,46:16,47:13,48:6,49:6,50:4,51:4,
          52:5,53:3,54:6,55:4,56:3,57:1,58:13,59:5,60:5,61:3,62:5,63:1,64:1,
          65:1,66:22
        };

        const { data: newBook, error: bookErr } = await supabase
          ?.from('bible_books')
          ?.insert({
            name: englishName,
            abbreviation: abbreviations?.[bookNum] || englishName?.substring(0, 4),
            testament: testaments,
            book_number: bookNum,
            chapter_count: chapterCounts?.[bookNum] || 1
          })
          ?.select('id')
          ?.single();

        if (bookErr) {
          // If unique constraint violation, the book already exists — fetch it
          const { data: retryBook } = await supabase
            ?.from('bible_books')
            ?.select('id')
            ?.eq('book_number', bookNum)
            ?.maybeSingle();
          if (retryBook?.id) bookIdMap?.set(bookNum, retryBook?.id);
        } else if (newBook?.id) {
          bookIdMap?.set(bookNum, newBook?.id);
        }
      }
    }

    // For each unique chapter, ensure bible_chapters record exists
    for (const [key, { bookNumber, chapter: chapterNum }] of bookChapterSet?.entries()) {
      const bookId = bookIdMap?.get(bookNumber);
      if (!bookId) continue;

      // Try to find existing chapter
      const { data: existingChapter } = await supabase
        ?.from('bible_chapters')
        ?.select('id')
        ?.eq('book_id', bookId)
        ?.eq('chapter_number', chapterNum)
        ?.maybeSingle();

      if (existingChapter?.id) {
        chapterIdMap?.set(key, existingChapter?.id);
      } else {
        // Count verses for this chapter from the verses array (approximate)
        const verseCount = verses?.filter(v => {
          const bn = parseInt(v?.book_number) || this.getBookNumber(v?.book_name);
          return bn === bookNumber && parseInt(v?.chapter) === chapterNum;
        })?.length;

        const { data: newChapter, error: chapErr } = await supabase
          ?.from('bible_chapters')
          ?.insert({
            book_id: bookId,
            chapter_number: chapterNum,
            verse_count: verseCount || 1
          })
          ?.select('id')
          ?.single();

        if (chapErr) {
          // Unique constraint violation — fetch existing
          const { data: retryChap } = await supabase
            ?.from('bible_chapters')
            ?.select('id')
            ?.eq('book_id', bookId)
            ?.eq('chapter_number', chapterNum)
            ?.maybeSingle();
          if (retryChap?.id) chapterIdMap?.set(key, retryChap?.id);
        } else if (newChapter?.id) {
          chapterIdMap?.set(key, newChapter?.id);
        }
      }
    }

    return chapterIdMap;
  },

  // Enhanced import log creation with version support and FIXED total verses count
  async createImportLog(fileInfo, totalVerses = 0, versionData = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase?.from('bible_import_logs')?.insert({
          admin_user_id: user?.id,
          file_name: fileInfo?.fileName,
          file_size: fileInfo?.fileSize,
          file_type: fileInfo?.fileType,
          total_verses: totalVerses, // FIXED: Pass actual verse count
          version_name: versionData?.versionName || null,
          short_version_name: versionData?.shortVersionName || null,
          language: versionData?.language || 'English',
          detected_headers: fileInfo?.detectedHeaders || null,
          status: 'processing'
        })?.select()?.single();
        
      if (error) throw error;
      
      return {
        id: data?.id,
        fileName: data?.file_name,
        fileSize: data?.file_size,
        fileType: data?.file_type,
        status: data?.status,
        totalVerses: data?.total_verses,
        processedVerses: data?.processed_verses,
        versionName: data?.version_name,
        shortVersionName: data?.short_version_name,
        language: data?.language,
        detectedHeaders: data?.detected_headers,
        startedAt: data?.started_at
      };
    } catch (error) {
      console.error('Error creating import log:', error);
      throw error;
    }
  },

  // Update import log status
  async updateImportLog(logId, updates) {
    try {
      const { data, error } = await supabase?.from('bible_import_logs')?.update({
          status: updates?.status,
          processed_verses: updates?.processedVerses,
          error_message: updates?.errorMessage,
          completed_at: updates?.status === 'completed' || updates?.status === 'failed' ? new Date()?.toISOString() : null
        })?.eq('id', logId)?.select()?.single();
        
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating import log:', error);
      throw error;
    }
  },

  // NEW SIMPLIFIED IMPORT METHOD - replaces complex async processing
  async importBibleVersionSimplified(file, versionData) {
    try {
      console.log('Starting simplified Bible import (JSON)...', { fileName: file?.name, versionData });

      // Parse JSON file content
      const fileContent = await this.readFileContent(file);
      let parsedData;

      try {
        parsedData = JSON.parse(fileContent);
      } catch (e) {
        throw new Error('Invalid JSON file - could not parse content');
      }

      // Extract metadata and verses from your JSON format
      const metadata = parsedData?.metadata || {};
      let verses = parsedData?.verses || [];

      if (!verses?.length) {
        throw new Error('No verses found in the JSON file');
      }

      console.log(`JSON parsed: ${verses?.length} verses found.`);

      // Step 1: Get authenticated user
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      // Step 2: Create Bible version
      const versionPayload = {
        version_name: versionData?.versionName || metadata?.name || 'Unknown Version',
        short_name: versionData?.shortVersionName || metadata?.shortname || 'UNK',
        language: versionData?.language || metadata?.lang_short || metadata?.lang || 'English',
        description: versionData?.description || metadata?.description || `Bible version imported from ${file?.name}`,
        created_by: user?.id
      };

      const { data: version, error: versionError } = await supabase
        ?.from('bible_versions')
        ?.insert(versionPayload)
        ?.select()
        ?.single();

      if (versionError) {
        throw new Error(`Failed to create Bible version: ${versionError.message}`);
      }

      console.log(`Bible version created: ${version?.version_name} (ID: ${version?.id})`);

      // Step 3: Create import log with valid creation_step
      const { data: importLog, error: logError } = await supabase
        ?.from('bible_import_logs')
        ?.insert({
          admin_user_id: user?.id,
          version_id: version?.id,
          file_name: file?.name,
          file_size: file?.size,
          file_type: 'json',
          total_verses: verses?.length,
          version_name: version?.version_name,
          short_version_name: version?.short_name,
          language: version?.language,
          status: 'processing',
          creation_step: 'file_upload'
        })
        ?.select()
        ?.single();

      if (logError) {
        console.warn('Failed to create import log:', logError);
      }

      // Step 4: Build chapter_id map (creates bible_books + bible_chapters as needed)
      console.log('Building chapter structure (bible_books + bible_chapters)...');
      const chapterIdMap = await this.buildChapterIdMap(verses);
      console.log(`Chapter map built: ${chapterIdMap?.size} unique chapters`);

      // Step 5: Process verses in batches
      const batchSize = 200;
      let processedCount = 0;
      let errorCount = 0;
      const totalBatches = Math.ceil(verses?.length / batchSize);

      for (let i = 0; i < verses?.length; i += batchSize) {
        const currentBatch = Math.floor(i / batchSize) + 1;
        const batch = verses?.slice(i, i + batchSize);

        try {
          // Map verse objects to bible_verses columns, including chapter_id
          const rows = batch?.map(v => {
            const bookNum = parseInt(v?.book_number) || this.getBookNumber(v?.book_name);
            const chap = parseInt(v?.chapter);
            const chapterKey = `${bookNum}_${chap}`;
            const chapterId = chapterIdMap?.get(chapterKey) || null;
            return {
              version_id: version?.id,
              chapter_id: chapterId,
              verse_number: parseInt(v?.verse) || 0,
              text: v?.text || '',
              reference: `${v?.book_name || ''} ${v?.chapter || ''}:${v?.verse || v?.verse_number || ''}`
            };
          });

          const { error: insertError } = await supabase
            ?.from('bible_verses')
            ?.insert(rows);

          if (insertError) {
            console.error(`Batch ${currentBatch} insert failed:`, insertError);
            errorCount += batch?.length;
          } else {
            processedCount += batch?.length;
            console.log(`Batch ${currentBatch} completed: ${batch?.length} verses inserted`);
          }

          // Small delay between batches
          if (currentBatch < totalBatches) {
            await new Promise(resolve => setTimeout(resolve, 50));
          }
        } catch (batchErr) {
          console.error(`Unexpected error in batch ${currentBatch}:`, batchErr);
          errorCount += batch?.length;
        }
      }

      // Step 6: Update import log status
      const finalStatus = errorCount === 0 ? 'completed' : (processedCount > 0 ? 'completed_with_errors' : 'failed');
      const successRate = verses?.length > 0 ? Math.round((processedCount / verses?.length) * 100) : 0;

      if (importLog?.id) {
        await supabase
          ?.from('bible_import_logs')
          ?.update({
            status: finalStatus,
            processed_verses: processedCount,
            error_message: errorCount > 0 ? `${errorCount} verses failed to import` : null,
            completed_at: new Date()?.toISOString()
          })
          ?.eq('id', importLog?.id);
      }

      if (processedCount === 0 && errorCount > 0) {
        throw new Error(`Import failed: all ${errorCount} verses could not be processed`);
      }

      return {
        success: true,
        processed: processedCount,
        totalVerses: verses?.length,
        successRate,
        versionId: version?.id,
        versionName: version?.version_name,
        errorCount
      };

    } catch (error) {
      console.error('Simplified JSON import failed:', error);
      return {
        success: false,
        error: error?.message || 'Import failed'
      };
    }
  },

  // HELPER: Read file content
  async readFileContent(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsText(file);
    });
  },

  // PARSE JSON CONTENT (enhanced for your format)
  parseJSONContent(content) {
    try {
      const parsed = JSON.parse(content);
      
      // Your specific JSON format
      if (parsed?.metadata && parsed?.verses) {
        return {
          totalRows: parsed?.verses?.length,
          verses: parsed?.verses,
          detectedHeaders: ['book_name', 'book', 'chapter', 'verse', 'text'],
          mappedHeaders: {
            book_name: 'book_name',
            chapter: 'chapter', 
            verse: 'verse',
            text: 'text'
          }
        };
      }

      throw new Error('Invalid JSON format - missing metadata or verses');
    } catch (error) {
      throw new Error(`JSON parsing failed: ${error.message}`);
    }
  },

  // Add new async import method for Option 3
  async importBibleVersionAsync(file, versionData = {}) {
    try {
      // Read file content
      const content = await this.readFileContent(file);
      let verses;
      
      // Parse based on file type
      if (file?.type?.includes('json')) {
        let parseResult = this.parseJSONContent(content);
        verses = parseResult?.verses;
      } else {
        let parseResult = await this.parseCSVContent(content);
        verses = parseResult?.verses;
      }

      if (!verses || verses?.length === 0) {
        throw new Error('No valid verses found in the file');
      }

      // Prepare file info
      const fileInfo = {
        fileName: file?.name,
        fileSize: file?.size,
        fileType: file?.type
      };

      console.log('Starting async Bible import (Option 3):', {
        versesCount: verses?.length,
        versionName: versionData?.versionName,
        fileInfo
      });

      // Start async import with micro-batching
      const { data: startResult, error: startError } = await supabase?.rpc(
        'start_async_bible_import_option3',
        {
          verses_data: verses,
          version_name: versionData?.versionName || this.generateVersionNameFromFileName(file?.name),
          short_name: versionData?.shortVersionName,
          language: versionData?.language || 'English',
          file_info: fileInfo,
          micro_batch_size: 250 // Small batches to prevent timeouts
        }
      );

      if (startError) {
        console.error('Async import start error:', startError);
        throw new Error(startError.message || 'Failed to start async import');
      }

      if (!startResult?.success) {
        throw new Error(startResult?.error || 'Async import initialization failed');
      }

      return {
        success: true,
        importLogId: startResult?.import_log_id,
        versionId: startResult?.version_id,
        versionName: startResult?.version_name,
        queueStats: startResult?.queue_stats,
        message: 'Async import started successfully. Processing in background.',
        processingType: 'async_option3',
        totalBatches: startResult?.queue_stats?.total_batches,
        microBatchSize: startResult?.micro_batch_size
      };

    } catch (error) {
      console.error('Async Bible import error:', error);
      return {
        success: false,
        error: error?.message || 'Unknown error occurred during async import'
      };
    }
  },

  // Process next batch in async queue
  async processNextAsyncBatch(importLogId) {
    try {
      const { data: result, error } = await supabase?.rpc(
        'process_next_async_batch',
        {
          import_log_id_param: importLogId
        }
      );

      if (error) {
        console.error('Next batch processing error:', error);
        throw new Error(error.message || 'Failed to process next batch');
      }

      return {
        success: true,
        ...result
      };

    } catch (error) {
      console.error('Process next batch error:', error);
      return {
        success: false,
        error: error?.message || 'Failed to process next batch'
      };
    }
  },

  // Get async import progress
  async getAsyncImportProgress(importLogId) {
    try {
      const { data: progress, error } = await supabase?.rpc(
        'get_async_import_progress',
        {
          import_log_id_param: importLogId
        }
      );

      if (error) {
        console.error('Progress fetch error:', error);
        throw new Error(error.message || 'Failed to fetch import progress');
      }

      if (!progress?.success) {
        throw new Error(progress?.error || 'Failed to get progress information');
      }

      return {
        success: true,
        progress: {
          totalVerses: progress?.total_verses,
          processedVerses: progress?.processed_verses,
          errorCount: progress?.error_count,
          progressPercentage: progress?.progress_percentage,
          totalBatches: progress?.total_batches,
          completedBatches: progress?.completed_batches,
          failedBatches: progress?.failed_batches,
          processingBatches: progress?.processing_batches,
          status: progress?.status,
          versionName: progress?.version_name,
          estimatedCompletion: progress?.estimated_completion,
          lastBatchCompletedAt: progress?.last_batch_completed_at,
          queueStats: progress?.queue_stats
        }
      };

    } catch (error) {
      console.error('Get progress error:', error);
      return {
        success: false,
        error: error?.message || 'Failed to get import progress'
      };
    }
  },

  // NEW: Frontend-Controlled Batching Import (Option 2) - Prevents Timeout Errors
  async importBibleVersionBatched(file, versionData = null) {
    try {
      console.log('Starting frontend-controlled batch import (Option 2)...');

      // Step 1: Parse file content first
      const fileContent = await this.readFileContent(file);
      let parseResult;

      const ext = file?.name?.split('.')?.pop()?.toLowerCase();
      const isJson = ext === 'json' || file?.type?.includes('json');

      if (isJson) {
        parseResult = this.parseJSONContent(fileContent);
      } else {
        parseResult = await this.parseCSVContent(fileContent);
      }

      if (!parseResult?.verses?.length) {
        throw new Error('No valid verses found in the file');
      }

      console.log(`File parsed: ${parseResult?.verses?.length} verses found.`);

      // Step 2: Create Bible version first
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const versionPayload = {
        version_name: versionData?.versionName || this.generateVersionNameFromFileName(file?.name),
        short_name: versionData?.shortVersionName || this.generateShortVersionName(versionData?.versionName || file?.name),
        language: versionData?.language || 'English',
        description: versionData?.description || `Bible version imported from ${file?.name}`,
        created_by: user?.id
      };

      const { data: version, error: versionError } = await supabase
        ?.from('bible_versions')
        ?.insert(versionPayload)
        ?.select()
        ?.single();

      if (versionError) {
        throw new Error(`Failed to create Bible version: ${versionError.message}`);
      }

      console.log(`Bible version created: ${version?.version_name} (ID: ${version?.id})`);

      // Step 3: Create import log
      const { data: importLog, error: logError } = await supabase
        ?.from('bible_import_logs')
        ?.insert({
          admin_user_id: user?.id,
          version_id: version?.id,
          file_name: file?.name,
          file_size: file?.size,
          file_type: isJson ? 'json' : 'csv',
          total_verses: parseResult?.verses?.length,
          version_name: version?.version_name,
          short_version_name: version?.short_name,
          language: version?.language,
          status: 'processing',
          creation_step: 'file_upload'
        })
        ?.select()
        ?.single();

      if (logError) {
        console.warn('Failed to create import log:', logError);
      }

      // Step 4: Build chapter_id map (creates bible_books + bible_chapters as needed)
      console.log('Building chapter structure (bible_books + bible_chapters)...');
      const chapterIdMap = await this.buildChapterIdMap(parseResult?.verses);
      console.log(`Chapter map built: ${chapterIdMap?.size} unique chapters`);

      // Step 5: Process verses in frontend-controlled batches
      const batchSize = 200;
      let processedCount = 0;
      let errorCount = 0;
      const totalBatches = Math.ceil(parseResult?.verses?.length / batchSize);

      for (let i = 0; i < parseResult?.verses?.length; i += batchSize) {
        const currentBatch = Math.floor(i / batchSize) + 1;
        const batch = parseResult?.verses?.slice(i, i + batchSize);

        try {
          // Map verse objects to bible_verses columns, including chapter_id
          const rows = batch?.map(v => {
            const bookNum = parseInt(v?.book_number) || this.getBookNumber(v?.book_name);
            const chap = parseInt(v?.chapter);
            const chapterKey = `${bookNum}_${chap}`;
            const chapterId = chapterIdMap?.get(chapterKey) || null;
            return {
              version_id: version?.id,
              chapter_id: chapterId,
              verse_number: parseInt(v?.verse) || 0,
              text: v?.text || '',
              reference: `${v?.book_name || ''} ${v?.chapter || ''}:${v?.verse || v?.verse_number || ''}`
            };
          });

          const { error: insertError } = await supabase
            ?.from('bible_verses')
            ?.insert(rows);

          if (insertError) {
            console.error(`Batch ${currentBatch} insert failed:`, insertError);
            errorCount += batch?.length;
          } else {
            processedCount += batch?.length;
            console.log(`Batch ${currentBatch} completed: ${batch?.length} verses inserted`);
          }

          // Small delay between batches
          if (currentBatch < totalBatches) {
            await new Promise(resolve => setTimeout(resolve, 50));
          }
        } catch (batchErr) {
          console.error(`Unexpected error in batch ${currentBatch}:`, batchErr);
          errorCount += batch?.length;
        }
      }

      // Step 6: Update import log status
      const finalStatus = errorCount === 0 ? 'completed' : (processedCount > 0 ? 'completed_with_errors' : 'failed');
      const successRate = parseResult?.verses?.length > 0 ? Math.round((processedCount / parseResult?.verses?.length) * 100) : 0;

      if (importLog?.id) {
        await supabase
          ?.from('bible_import_logs')
          ?.update({
            status: finalStatus,
            processed_verses: processedCount,
            error_message: errorCount > 0 ? `${errorCount} verses failed to import` : null,
            completed_at: new Date()?.toISOString()
          })
          ?.eq('id', importLog?.id);
      }

      console.log(`Batch import completed: ${processedCount}/${parseResult?.verses?.length} verses (${successRate}% success rate)`);

      return {
        success: processedCount > 0,
        processed: processedCount,
        totalVerses: parseResult?.verses?.length,
        successRate,
        versionId: version?.id,
        versionName: version?.version_name,
        batchProcessing: true,
        batchSize,
        totalBatches,
        importLogId: importLog?.id,
        method: 'direct_insert_batched'
      };

    } catch (error) {
      console.error('Frontend-controlled batch import failed:', error);
      throw new Error(`Frontend batch import failed: ${error.message}`);
    }
  },

  // NEW: Single batch processing function for small verse batches
  async processSingleBatch(versionId, versesBatch, batchNumber, totalBatches) {
    try {
      console.log(`Processing batch ${batchNumber}/${totalBatches} with ${versesBatch?.length} verses`);

      // Use a simpler database function that processes one small batch at a time
      const { data: result, error } = await supabase?.rpc('insert_verses_batch_simple', {
        target_version_id: versionId,
        verses_batch: versesBatch
      });

      if (error) {
        console.error(`Batch processing error:`, error);
        return {
          success: false,
          error: error?.message,
          processed_count: 0
        };
      }

      return {
        success: true,
        processed_count: result?.processed_count || versesBatch?.length,
        batch_number: batchNumber,
        total_batches: totalBatches
      };

    } catch (error) {
      console.error('Single batch processing error:', error);
      return {
        success: false,
        error: error?.message,
        processed_count: 0
      };
    }
  },

  // NEW: Option 2 - True Individual Processing (each verse processed individually)
  async importBibleVersionIndividual(file, versionData = {}) {
    try {
      console.log('Starting Option 2: True Individual Processing...');

      // Step 1: Parse file content
      const fileContent = await this.readFileContent(file);
      let verses;
      
      if (file?.type?.includes('json')) {
        let parsedData = JSON.parse(fileContent);
        verses = parsedData?.verses || [];
      } else {
        let parseResult = await this.parseCSVContent(fileContent);
        verses = parseResult?.verses;
      }

      if (!verses || verses?.length === 0) {
        throw new Error('No valid verses found in the file');
      }

      console.log(`Option 2: Processing ${verses?.length} verses individually (one INSERT per verse)`);

      // Prepare version metadata for individual processing
      const versionMetadata = {
        versionName: versionData?.versionName || this.generateVersionNameFromFileName(file?.name),
        shortVersionName: versionData?.shortVersionName || this.generateShortVersionName(versionData?.versionName || file?.name),
        language: versionData?.language || 'English',
        description: versionData?.description || `Bible version imported via Option 2: True Individual Processing from ${file?.name}`,
        sourceFileName: file?.name,
        fileSize: file?.size,
        fileType: file?.type?.includes('json') ? 'json' : 'csv'
      };

      // Call the new Option 2 function for true individual processing
      const { data: result, error } = await supabase?.rpc(
        'process_bible_import_true_individual', 
        {
          p_version_data: versionMetadata,
          p_verses_data: verses
        }
      );

      if (error) {
        console.error('Option 2 individual processing error:', error);
        throw new Error(`Option 2 processing failed: ${error.message}`);
      }

      if (!result?.success) {
        throw new Error(result?.error || 'Option 2 individual processing failed');
      }

      console.log(`Option 2 completed: ${result?.processed} verses processed individually`);

      return {
        success: true,
        processed: result?.processed,
        totalVerses: result?.total_verses,
        errorCount: result?.error_count || 0,
        skipped: result?.skipped || 0,
        successRate: result?.success_rate,
        versionId: result?.version_id,
        versionName: versionMetadata?.version_name,
        importLogId: result?.import_log_id,
        method: 'option2_true_individual_processing',
        processingType: result?.processing_type,
        sqlCompliant: result?.sql_compliant,
        strictAliases: result?.strict_aliases,
        individualInserts: true,
        performance: result?.performance,
        message: `Option 2 Success: ${result?.processed} verses processed with individual INSERT statements (SQL compliant with strict aliases)`
      };

    } catch (error) {
      console.error('Option 2 true individual processing failed:', error);
      throw new Error(`Option 2 Individual Import Failed: ${error.message}`);
    }
  },

  /**
   * Process Bible import with enhanced verbose logging
   * @param {Array} verseData - Array of verse objects
   * @param {string} adminUserId - Admin user ID
   * @param {Object} versionInfo - Bible version information
   * @returns {Object} Import result with verbose logging support
   */
  async processBibleImportEnhanced(verseData, adminUserId, versionInfo = null) {
    try {
      // Validate input data
      if (!Array.isArray(verseData) || verseData?.length === 0) {
        return {
          success: false,
          error: 'Invalid or empty verse data provided',
          errorCode: 'INVALID_VERSE_DATA'
        };
      }

      if (!adminUserId) {
        return {
          success: false,
          error: 'Admin user ID is required',
          errorCode: 'MISSING_ADMIN_USER'
        };
      }

      // Call the enhanced import function with verbose logging
      const { data, error } = await supabase?.rpc('process_bible_import_admin_enhanced', {
        file_data: verseData,
        admin_user_id: adminUserId,
        version_info: versionInfo
      });

      if (error) {
        console.error('Enhanced Bible import error:', error);
        return {
          success: false,
          error: error?.message || 'Database import failed',
          errorCode: 'DATABASE_ERROR',
          sqlError: error
        };
      }

      // Return the enhanced result with logging information
      return {
        success: data?.success || false,
        processed: data?.processed || 0,
        errors: data?.errors || [],
        errorCount: data?.error_count || 0,
        versionId: data?.version_id,
        versionCreated: data?.version_created || false,
        autoGeneratedVersion: data?.auto_generated_version || false,
        importLogId: data?.import_log_id,
        logFileAvailable: data?.log_file_available || false,
        executionDuration: data?.execution_duration_seconds || 0,
        versesPerSecond: data?.verses_per_second || 0,
        error: data?.success === false ? data?.error : null,
        errorCode: data?.success === false ? data?.error_code : null
      };
    } catch (error) {
      console.error('Unexpected error in enhanced Bible import:', error);
      return {
        success: false,
        error: error?.message || 'Unexpected import error',
        errorCode: 'UNEXPECTED_ERROR',
        processed: 0,
        errors: []
      };
    }
  },

  // Get all Bible versions with verse count stats
  async getBibleVersionsWithStats() {
    try {
      const { data, error } = await supabase
        ?.from('bible_versions')
        ?.select(`
          id,
          version_name,
          short_name,
          description,
          language,
          is_enabled,
          created_at,
          updated_at
        `)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      // Get verse counts per version
      const versionsWithStats = await Promise.all(
        (data || [])?.map(async (version) => {
          const { count } = await supabase
            ?.from('bible_verses')
            ?.select('id', { count: 'exact', head: true })
            ?.eq('version_id', version?.id);

          return {
            id: version?.id,
            versionName: version?.version_name,
            shortName: version?.short_name,
            description: version?.description,
            language: version?.language,
            isActive: version?.is_enabled,
            createdAt: version?.created_at,
            updatedAt: version?.updated_at,
            totalVerses: count || 0
          };
        })
      );

      return versionsWithStats;
    } catch (error) {
      console.error('Error fetching Bible versions with stats:', error);
      throw error;
    }
  },

  // Delete a single Bible version and all its verses
  async deleteBibleVersion(versionId) {
    try {
      // Delete verses first
      const { error: versesError } = await supabase
        ?.from('bible_verses')
        ?.delete()
        ?.eq('version_id', versionId);

      if (versesError) throw versesError;

      // Delete the version record
      const { error: versionError } = await supabase
        ?.from('bible_versions')
        ?.delete()
        ?.eq('id', versionId);

      if (versionError) throw versionError;

      return { success: true };
    } catch (error) {
      console.error('Error deleting Bible version:', error);
      return { success: false, error: error?.message || 'Failed to delete Bible version' };
    }
  },

  // Bulk delete multiple Bible versions
  async bulkDeleteBibleVersions(versionIds) {
    try {
      if (!versionIds?.length) return { success: false, error: 'No versions selected' };

      // Delete verses for all selected versions
      const { error: versesError } = await supabase
        ?.from('bible_verses')
        ?.delete()
        ?.in('version_id', versionIds);

      if (versesError) throw versesError;

      // Delete the version records
      const { error: versionError } = await supabase
        ?.from('bible_versions')
        ?.delete()
        ?.in('id', versionIds);

      if (versionError) throw versionError;

      return { success: true, deletedCount: versionIds?.length };
    } catch (error) {
      console.error('Error bulk deleting Bible versions:', error);
      return { success: false, error: error?.message || 'Failed to delete Bible versions' };
    }
  },

  // Toggle active/inactive status of a Bible version
  async toggleBibleVersionStatus(versionId, newStatus) {
    try {
      const { error } = await supabase
        ?.from('bible_versions')
        ?.update({ is_enabled: newStatus, updated_at: new Date()?.toISOString() })
        ?.eq('id', versionId);

      if (error) throw error;

      return { success: true, isActive: newStatus };
    } catch (error) {
      console.error('Error toggling Bible version status:', error);
      return { success: false, error: error?.message || 'Failed to update version status' };
    }
  },

  // Get detailed info for a single Bible version
  async getBibleVersionDetails(versionId) {
    try {
      const { data, error } = await supabase
        ?.from('bible_versions')
        ?.select('*')
        ?.eq('id', versionId)
        ?.single();

      if (error) throw error;

      const { count } = await supabase
        ?.from('bible_verses')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('version_id', versionId);

      return {
        id: data?.id,
        versionName: data?.version_name,
        shortName: data?.short_name,
        description: data?.description,
        language: data?.language,
        isActive: data?.is_enabled,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
        totalVerses: count || 0
      };
    } catch (error) {
      console.error('Error fetching Bible version details:', error);
      throw error;
    }
  },

  // Export/download a Bible version as JSON
  async downloadBibleVersionExport(versionId, versionName) {
    try {
      const { data, error } = await supabase
        ?.from('bible_verses')
        ?.select('book_name, chapter_number, verse_number, verse_text, reference')
        ?.eq('version_id', versionId)
        ?.order('book_name')
        ?.order('chapter_number')
        ?.order('verse_number');

      if (error) throw error;

      const exportData = {
        version: versionName,
        exportedAt: new Date()?.toISOString(),
        totalVerses: data?.length || 0,
        verses: data || []
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${versionName?.replace(/\s+/g, '_')}_export.json`;
      document.body?.appendChild(a);
      a?.click();
      document.body?.removeChild(a);
      URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error('Error exporting Bible version:', error);
      throw error;
    }
  },

  // ============================================================
  // BIBLE LANGUAGE MANAGEMENT
  // ============================================================

  // List all languages that have book name mappings
  async listBibleLanguages() {
    try {
      const { data, error } = await supabase?.rpc('admin_list_bible_languages');
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error listing bible languages:', error);
      throw error;
    }
  },

  // Get all mappings for a specific language
  async getLanguageMappings(languageName) {
    try {
      const { data, error } = await supabase?.rpc('admin_get_language_mappings', {
        p_language_name: languageName
      });
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting language mappings:', error);
      throw error;
    }
  },

  // Import book name mappings from JSON
  // JSON format: [{id: 1, name: "Genèse", shortname: null, matching1: null, matching2: null}, ...]
  // id = book number (1-66), name = book name in target language
  async importLanguageMappings(languageName, booksJson) {
    try {
      let books;
      if (typeof booksJson === 'string') {
        books = JSON.parse(booksJson);
      } else {
        books = booksJson;
      }

      if (!Array.isArray(books)) {
        throw new Error('JSON must be an array of book objects');
      }

      const results = { inserted: 0, updated: 0, reassigned: 0, failed: 0, errors: [] };

      for (const book of books) {
        const bookNumber = parseInt(book?.id);
        if (!bookNumber || bookNumber < 1 || bookNumber > 66) {
          results.failed++;
          results?.errors?.push(`Invalid book id: ${book?.id}`);
          continue;
        }

        // Collect all name variations for this book
        const namesToImport = [];
        if (book?.name?.trim()) namesToImport?.push(book?.name?.trim());
        if (book?.shortname?.trim()) namesToImport?.push(book?.shortname?.trim());
        if (book?.matching1?.trim()) namesToImport?.push(book?.matching1?.trim());
        if (book?.matching2?.trim()) namesToImport?.push(book?.matching2?.trim());

        for (const nameVariation of namesToImport) {
          try {
            const { data, error } = await supabase?.rpc('admin_upsert_book_name_mapping', {
              p_book_number: bookNumber,
              p_variation_name: nameVariation,
              p_language_name: languageName?.toLowerCase()?.trim(),
              p_source_type: 'language_import'
            });

            if (error) {
              results.failed++;
              results?.errors?.push(`Book ${bookNumber} "${nameVariation}": ${error?.message}`);
            } else {
              const action = data?.action;
              if (action === 'inserted') results.inserted++;
              else if (action === 'updated') results.updated++;
              else if (action === 'reassigned') results.reassigned++;
            }
          } catch (err) {
            results.failed++;
            results?.errors?.push(`Book ${bookNumber} "${nameVariation}": ${err?.message}`);
          }
        }
      }

      return results;
    } catch (error) {
      console.error('Error importing language mappings:', error);
      throw error;
    }
  },

  // Delete all mappings for a language
  async deleteLanguageMappings(languageName) {
    try {
      const { data, error } = await supabase?.rpc('admin_delete_language_mappings', {
        p_language_name: languageName
      });
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error deleting language mappings:', error);
      throw error;
    }
  }
};